<script>
import Datepicker from 'vuejs-datepicker'
import VueTagsInput from '@johmun/vue-tags-input'
import store from '@state/store'
import {
  EVENT_MINIMUM_AGES as eventMinimumAges,
  EVENT_DISTANCE_THRESHOLDS as eventDistanceThresholds,
} from '@events/constant'
import { debounce } from 'debounce'
import { add, startOfMonth, endOfMonth, isSameDay } from 'date-fns'
import isArray from 'lodash/isArray'
import {
  getFiltersFromCurrentUrl,
  prepareTagsForVueTagsInput,
  setFiltersToCurrentUrl,
} from '@events/utils'

const initialFiltersState = {
  from_date: '',
  to_date: '',
  with_ongoing: true,

  availability: false,
  availability_from: '',
  availability_to: '',
  cause_ids: [],
  distance: '',
  interacting_with_ids: [],
  keyword: '',
  location: '',
  min_age: '',
  min_people: '',
  needs_leader: false,
  ongoing: false,
  school_grade_ids: [],
  school_ids: [],
  tag_ids: [],
}

const initialTagsState = {
  items: [],
  input: '',
  selected: [],
}

export default {
  name: 'CompanyEventsFilters',
  components: { VueTagsInput, Datepicker },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      filters: { ...initialFiltersState },
      tags: { ...initialTagsState },
      eventsFiltersInput: {},
      eventsFilters: {},
      selectedEventsFilters: {},
      eventMinimumAges,
      eventDistanceThresholds,
    }
  },
  computed: {
    date() {
      return {
        from_date: this.$store.state.events.filters.from_date,
        to_date: this.$store.state.events.filters.to_date,
      }
    },
    tagsInput() {
      return this.tags.input
    },
    isEligibleToClearDate() {
      return !!(
        !isSameDay(this.filters.from_date, startOfMonth(new Date())) ||
        !isSameDay(this.filters.to_date, endOfMonth(new Date())) ||
        this.filters.ongoing
      )
    },
    isEligibleToClearAvailability() {
      return !!(
        this.filters.availability_from ||
        this.filters.availability_to ||
        this.filters.needs_leader
      )
    },
  },
  watch: {
    tagsInput: 'getEventTags',
    date() {
      this.filters = { ...this.filters, ...this.date }
    },
    filters: {
      async handler() {
        const filledFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => {
            if (isArray(value) && !value.length) return false
            return !!value
          })
        )

        setFiltersToCurrentUrl(filledFilters)

        await store.dispatch('events/fetchFilteredEvents', {
          companyId: this.companyId,
          filters: filledFilters,
        })
      },
      deep: true,
    },
  },
  created() {
    this.getEventsFilters()
  },
  methods: {
    setFiltersFromCurrentUrl() {
      this.filters = getFiltersFromCurrentUrl(this.filters, this.$route)

      const selectedFilters = {}

      if (this.filters.cause_ids.length) {
        selectedFilters.causes = this.eventsFilters.causes.filter(({ id }) =>
          this.filters.cause_ids.includes(id)
        )
      }

      if (this.filters.interacting_with_ids.length) {
        selectedFilters.interacting_with = this.eventsFilters.interacting_with.filter(
          ({ id }) => this.filters.interacting_with_ids.includes(id)
        )
      }

      if (this.filters.school_grade_ids.length) {
        selectedFilters.school_grades = this.eventsFilters.school_grades.filter(
          ({ id }) => this.filters.school_grade_ids.includes(id)
        )
      }

      this.selectedEventsFilters = {
        ...this.selectedEventsFilters,
        ...selectedFilters,
      }

      if (this.filters.tag_ids.length) {
        store
          .dispatch('events/fetchTags', { tagIds: this.filters.tag_ids })
          .then(({ data }) => {
            if (!data) return
            this.tags.selected = data.map(({ id, name }) => ({
              id,
              text: name,
            }))
          })
      }
    },
    set10DaysOpenFilter() {
      this.filters = {
        ...this.filters,
        from_date: new Date(),
        to_date: add(new Date(), { days: 10 }),
        availability_from: 1,
      }
    },
    clearFilters() {
      this.filters = {
        ...initialFiltersState,
        from_date: this.date.from_date,
        to_date: this.date.to_date,
      }
      this.selectedEventsFilters = {}
      this.tags = { ...initialTagsState }
    },
    clearDateFilters() {
      this.filters = {
        ...this.filters,
        from_date: startOfMonth(new Date()),
        to_date: endOfMonth(new Date()),
        ongoing: false,
      }
    },
    clearAvailabilityFilters() {
      this.filters = {
        ...this.filters,
        availability_from: '',
        availability_to: '',
        needs_leader: false,
      }
    },
    clearEventFilter(filter, filterIds) {
      this.filters = {
        ...this.filters,
        [filterIds]: [],
      }
      delete this.selectedEventsFilters[filter]
    },
    clearTagFilters() {
      this.filters = {
        ...this.filters,
        tag_ids: [],
      }
      this.tags = { ...initialTagsState }
    },
    setFilterField: debounce(async function(e, field, minLength = 0) {
      const value = (e.target && e.target.value) || e
      this.filters[field] = value.length >= minLength ? value : ''
    }, 500),

    getEventsFiltersItems(filter) {
      return this.eventsFilters[filter] || []
    },

    getEventsFilters() {
      return store
        .dispatch('events/fetchEventsFilters')
        .then((data) => {
          this.eventsFilters = data
          this.setFiltersFromCurrentUrl()
          return data
        })
        .catch(() => console.warn('Oh. Something went wrong'))
    },

    updateEventsFilters(filter, filterIds, newTags) {
      this.selectedEventsFilters = {
        ...this.selectedEventsFilters,
        [filter]: newTags,
      }
      this.filters = {
        ...this.filters,
        [filterIds]: newTags.map(({ id }) => id),
      }
    },

    getEventTags: debounce(async function getEventTags() {
      if (!this.tagsInput) return

      await store
        .dispatch('events/fetchFilteredTags', {
          startingWith: this.tagsInput,
        })
        .then(({ data }) => {
          if (!data) return
          this.tags.items = prepareTagsForVueTagsInput(data)
        })
    }, 500),

    updateEventTags(newTags) {
      this.tags.items = []
      this.tags.selected = newTags
      this.filters.tag_ids = this.tags.selected.map(({ id }) => id)
    },
  },
}
</script>

<template>
  <div class="flex my-6 space-x-4 px-22">
    <div class="grid flex-1 grid-cols-6 gap-x-2 gap-y-3">
      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': isEligibleToClearDate }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="isEligibleToClearDate"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="clearDateFilters"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">Date</span>
        </div>

        <template slot="popover">
          <div class="flex items-center mb-4.5">
            <Datepicker
              v-model="filters.from_date"
              input-class="text-center border-b border-black cursor-pointer w-26"
            />

            <span class="text-black mx-2.5 text-basel">to</span>

            <Datepicker
              v-model="filters.to_date"
              :disabled-dates="{ to: filters.from_date }"
              input-class="text-center border-b border-black cursor-pointer w-26"
            />
          </div>

          <label class="inline-flex items-center cursor-pointer">
            <input
              v-model="filters.ongoing"
              class="mr-2 checkbox"
              type="checkbox"
            />
            <span>Ongoing</span>
          </label>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': isEligibleToClearAvailability }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="isEligibleToClearAvailability"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="clearAvailabilityFilters"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">Availability</span>
        </div>

        <template slot="popover">
          <div class="flex items-center">
            <input
              :value="filters.availability_from"
              :min="0"
              :max="filters.availability_to"
              class="text-center border-b border-black cursor-pointer w-26"
              type="number"
              @input="setFilterField($event, 'availability_from', 1)"
            />

            <span class="text-black mx-2.5 text-basel">to</span>

            <input
              :value="filters.availability_to"
              :min="filters.availability_from"
              class="text-center border-b border-black cursor-pointer w-26"
              type="number"
              @input="setFilterField($event, 'availability_to', 1)"
            />
          </div>

          <span class="block text-right text-black text-sml mt-1 mb-4.5">
            (Spots available)
          </span>

          <label class="inline-flex items-center cursor-pointer">
            <input
              v-model="filters.needs_leader"
              class="mr-2 checkbox"
              type="checkbox"
            />
            <span>Volunteer opportunities without a leader</span>
          </label>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': selectedEventsFilters.causes }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="selectedEventsFilters.causes"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="clearEventFilter('causes', 'cause_ids')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{
              (selectedEventsFilters.causes &&
                selectedEventsFilters.causes[0].text) ||
                'Opportunity Type'
            }}
          </span>
        </div>

        <template slot="popover">
          <div class="w-58">
            <VueTagsInput
              v-model="eventsFiltersInput.causes"
              :autocomplete-min-length="0"
              :add-on-blur="false"
              :add-only-from-autocomplete="true"
              :autocomplete-items="getEventsFiltersItems('causes')"
              :tags="selectedEventsFilters.causes"
              placeholder="Filter by opportunity type"
              @tags-changed="
                (tags) => updateEventsFilters('causes', 'cause_ids', tags)
              "
            />
          </div>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': selectedEventsFilters.schools }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="selectedEventsFilters.schools"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="clearEventFilter('schools', 'school')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{
              (selectedEventsFilters.schools &&
                selectedEventsFilters.schools[0].text) ||
                'School'
            }}
          </span>
        </div>

        <template slot="popover">
          <div class="w-58">
            <VueTagsInput
              v-model="eventsFiltersInput.schools"
              :autocomplete-min-length="0"
              :add-on-blur="false"
              :add-only-from-autocomplete="true"
              :autocomplete-items="getEventsFiltersItems('schools')"
              :tags="selectedEventsFilters.schools"
              placeholder="Filter by school"
              @tags-changed="
                (tags) => updateEventsFilters('schools', 'school', tags)
              "
            />
          </div>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': selectedEventsFilters.school_grades }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="selectedEventsFilters.school_grades"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="
              clearEventFilter('school_grades', 'school_grade_ids')
            "
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{
              (selectedEventsFilters.school_grades &&
                selectedEventsFilters.school_grades[0].text) ||
                'School Level'
            }}
          </span>
        </div>

        <template slot="popover">
          <div class="w-58">
            <VueTagsInput
              v-model="eventsFiltersInput.school_grades"
              :autocomplete-min-length="0"
              :add-on-blur="false"
              :add-only-from-autocomplete="true"
              :autocomplete-items="getEventsFiltersItems('school_grades')"
              :tags="selectedEventsFilters.school_grades"
              placeholder="Filter by School Level"
              @tags-changed="
                (tags) =>
                  updateEventsFilters('school_grades', 'school_grade_ids', tags)
              "
            />
          </div>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': selectedEventsFilters.interacting_with }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="selectedEventsFilters.interacting_with"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="
              clearEventFilter('interacting_with', 'interacting_with_ids')
            "
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{
              (selectedEventsFilters.interacting_with &&
                selectedEventsFilters.interacting_with[0].text) ||
                'Interacting with'
            }}
          </span>
        </div>

        <template slot="popover">
          <div class="w-58">
            <VueTagsInput
              v-model="eventsFiltersInput.interacting_with"
              :autocomplete-min-length="0"
              :add-on-blur="false"
              :add-only-from-autocomplete="true"
              :autocomplete-items="getEventsFiltersItems('interacting_with')"
              :tags="selectedEventsFilters.interacting_with"
              placeholder="Filter by interacting"
              @tags-changed="
                (tags) =>
                  updateEventsFilters(
                    'interacting_with',
                    'interacting_with_ids',
                    // eslint-disable-next-line vue/comma-dangle
                    tags
                  )
              "
            />
          </div>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': tags.selected.length }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="tags.selected.length"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="clearTagFilters"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{ (tags.selected.length && tags.selected[0].text) || 'Tag' }}
          </span>
        </div>

        <template slot="popover">
          <div class="w-58">
            <VueTagsInput
              v-model="tags.input"
              :tags="tags.selected"
              :autocomplete-items="tags.items"
              :add-only-from-autocomplete="true"
              :add-on-blur="false"
              placeholder="Filter by event tag"
              @tags-changed="updateEventTags"
            />
          </div>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': filters.keyword }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="filters.keyword"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="setFilterField('', 'keyword')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{ filters.keyword || 'Keyword' }}
          </span>
        </div>

        <template slot="popover">
          <input
            class="text-center border-b border-black cursor-pointer w-58"
            placeholder="Filter by Keyword"
            :value="filters.keyword"
            @input="setFilterField($event, 'keyword', 3)"
          />
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': filters.min_people }"
          class="inline-flex items-center w-full filter-dropdown"
          title="Minimum volunteers needed"
        >
          <button
            v-if="filters.min_people"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="setFilterField('', 'min_people')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            Minimum volunteers needed
          </span>
        </div>

        <template slot="popover">
          <input
            :value="filters.min_people"
            :min="0"
            class="text-center border-b border-black cursor-pointer w-58"
            type="number"
            placeholder="Filter by minimum volunteers needed"
            @input="setFilterField($event, 'min_people', 1)"
          />
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': filters.min_age }"
          class="inline-flex items-center w-full filter-dropdown"
        >
          <button
            v-if="filters.min_age"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="setFilterField('', 'min_age')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{ filters.min_age || 'Volunteer Age' }}
          </span>
        </div>

        <template slot="popover">
          <ul>
            <li
              v-for="{ name, value } in eventMinimumAges"
              :key="value"
              class="cursor-pointer mx-2.5 text-basel"
              :class="filters.min_age === value ? 'text-blue' : 'text-black'"
              @click="setFilterField(value, 'min_age')"
            >
              {{ name }}
            </li>
          </ul>
        </template>
      </v-popover>

      <v-popover trigger="click" placement="bottom" :auto-hide="true">
        <div
          :class="{ 'text-blue': filters.distance }"
          class="inline-flex items-center w-full filter-dropdown"
          title="Distance from ZIP code"
        >
          <button
            v-if="filters.distance"
            class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
            @click.prevent="setFilterField('', 'distance')"
          >
            <BaseIcon name="close" />
          </button>
          <span class="mr-2 truncate">
            {{ filters.distance || 'Distance from ZIP code' }}
          </span>
        </div>

        <template slot="popover">
          <ul>
            <li
              v-for="{ name, value } in eventDistanceThresholds"
              :key="value"
              class="cursor-pointer mx-2.5 text-basel"
              :class="filters.distance === value ? 'text-blue' : 'text-black'"
              @click="setFilterField(value, 'distance')"
            >
              {{ name }}
            </li>
          </ul>
        </template>
      </v-popover>
    </div>

    <button
      class="inline-flex items-center text-base text-left button secondary-button"
      @click="clearFilters"
    >
      <BaseIcon name="close" class="mr-1" />
      <span>Clear Filters</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.vue-tags-input::v-deep {
  .ti-input {
    @apply border-0 border-b border-black rounded-none shadow-none;
  }
  .ti-new-tag-input {
    @apply text-center text-black;
  }
}
.v-popover::v-deep > div {
  @apply w-full;
}
/* stylelint-enable */
</style>
